import { ArticleNumber, DATA_AGENTS_ARTICLE_NUMBERS, SUBSCRIPTION_TYPES, TERMINAL_SUBSCRIPTION_ARTICLE_NUMBERS, } from '../organization/types';
/**
 * Returns all subscriptions as key value pairs, having the article number as
 * key and the name (long version) as their values for being available in the
 * UI via select-field.
 *
 * @param ownedArticleNumbers - article numbers of the organization's owned subscriptions
 * @param selectedArticleNumber - optionally the article number of the currently selected subscription.
 */
export function getPossibleArticleNumbers(ownedArticleNumbers, activeModulePrefixes, selectedArticleNumber) {
    const activeArticleNumbersSet = new Set(ownedArticleNumbers);
    return Array.from(SUBSCRIPTION_TYPES)
        .filter(([articleNumber, _articleInfo]) => {
        if (selectedArticleNumber === articleNumber) {
            // The currently selected article number should always be selectable.
            return true;
        }
        // No managed trial if existing subscription already contains a free trial (unless the currently selected
        // subscription is the free trial, then it's fine to switch between them)
        if (articleNumber === ArticleNumber.MANAGED_TRIAL &&
            activeArticleNumbersSet.has(ArticleNumber.FREE_TRIAL) &&
            selectedArticleNumber !== ArticleNumber.FREE_TRIAL) {
            return false;
        }
        // No free trial if existing subscription already contains a managed trial (unless the currently selected
        // subscription is the managed trial, then it's fine to switch between them)
        if (articleNumber === ArticleNumber.FREE_TRIAL &&
            activeArticleNumbersSet.has(ArticleNumber.MANAGED_TRIAL) &&
            selectedArticleNumber !== ArticleNumber.MANAGED_TRIAL) {
            return false;
        }
        // No packages of the same article class should be possible (except for managed trials and vr peak licenses)
        if (activeArticleNumbersSet.has(articleNumber) &&
            articleNumber !== ArticleNumber.MANAGED_TRIAL &&
            articleNumber !== ArticleNumber.VISIT_REPORT_PEAK &&
            articleNumber !== ArticleNumber.VISIT_REPORT_PEAK_VOICE_AI) {
            return false;
        }
        const isTerminalOption = TERMINAL_SUBSCRIPTION_ARTICLE_NUMBERS.has(articleNumber);
        const isCurrentlyTerminalSubscription = selectedArticleNumber && TERMINAL_SUBSCRIPTION_ARTICLE_NUMBERS.has(selectedArticleNumber);
        // Terminal subscriptions should not be able to have non-terminal options.
        if (isCurrentlyTerminalSubscription && !isTerminalOption) {
            return false;
        }
        // Existing non-terminal subscriptions (with the exception of MT) should not be able to have terminal options.
        if (selectedArticleNumber &&
            selectedArticleNumber !== ArticleNumber.MANAGED_TRIAL &&
            !isCurrentlyTerminalSubscription &&
            isTerminalOption) {
            return false;
        }
        // New subscriptions should not be able to select terminal options if terminal module is already active.
        if (!selectedArticleNumber && isTerminalOption && activeModulePrefixes.has('VRT')) {
            return false;
        }
        const dataAgentModulesForComparison = DATA_AGENTS_ARTICLE_NUMBERS;
        // DataAgents subscriptions should not be selectable if any DataAgents module is already active unless the
        // selected subscription is a DA subscription (to up-/downgrade). We still allow MT because there is no other way
        // around it right now but the person editing must ensure MT with DA enabled and DA subscription should not exist
        // at the same time.
        if (dataAgentModulesForComparison.has(articleNumber) &&
            // And there is no selected subscription or the selected subscription is not a DA subscription and not MT
            (!selectedArticleNumber ||
                (!dataAgentModulesForComparison.has(selectedArticleNumber) &&
                    selectedArticleNumber !== ArticleNumber.MANAGED_TRIAL)) &&
            activeModulePrefixes.has('DA ')) {
            return false;
        }
        return true;
    })
        .map(([articleNumber, articleInfo]) => ({
        articleNumber: articleNumber,
        name: articleInfo.name,
    }));
}
